// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { Icon } from "@/components/formatters/MXPIcon"
import { TerraLink } from "../TerraLink/TerraLink"
import { SpecialButton } from "../SpecialButton/SpecialButton"
import { CommonDescription } from "../CommonDescription/CommonDescription"
import { renderFocusedOrRegularImage } from "../../utils/renderFocusedOrRegularImage"
import { formatFocusArea } from "../../utils/formatFocusArea"
import { cardSrc, CARD_SOURCES } from "@/lib/heroImageUtils"

// import "./CardGeneral.css"

export interface CardGeneral {
  image?: {
    url: string
    alt: string
    height?: number
    width?: number
    focusArea?: string
  }
  imageType?: "asImage" | "asBackground"
  cardType?: "bound" | "unbound" | "categorized"
  eyebrow?: string
  eyebrowIcon?: string
  header?: string
  description?: string
  button?: TerraLink | SpecialButton
  truncate?: boolean
  truncateLength?: number
  inheritedClasses?: string
  componentImagePriority?: "high" | "auto" | "low"
  isLCP?: boolean
}

export const CardGeneral = ({
  image,
  imageType = "asBackground",
  cardType,
  eyebrow,
  eyebrowIcon,
  header,
  description,
  button,
  truncate,
  truncateLength,
  inheritedClasses,
  componentImagePriority,
  isLCP = false,
}: CardGeneral) => {
  const cardMode = cardType || "bound"
  const useLightSetting =
    cardMode == "bound" || cardMode == "categorized"
      ? "t2-color-setting--light"
      : ""

  const boundCardImageSizes = {
    desktop: {
      height: 200,
      width: 413,
      ratio: {
        height: 200,
        width: 413,
      },
    },
    tablet: {
      height: 200,
      width: 413,
      ratio: {
        height: 200,
        width: 413,
      },
    },
    mobile: {
      height: 200,
      width: 413,
      ratio: {
        height: 200,
        width: 413,
      },
    },
  }

  const unboundCardImageSizes = {
    desktop: {
      height: 200,
      width: 300,
      ratio: {
        height: 2,
        width: 3,
      },
    },
    tablet: {
      height: 200,
      width: 300,
      ratio: {
        height: 2,
        width: 3,
      },
    },
    mobile: {
      height: 200,
      width: 300,
      ratio: {
        height: 2,
        width: 3,
      },
    },
  }

  const cardTemplate = {
    // Bound Card Template
    bound: {
      cardClasses: `t2-c w-full bg-gray-m1 shadow-card max-w-[413px] t2-dark:text-black t2:bg-white mb-5 flex flex-col rounded-[10px] overflow-hidden ${useLightSetting} ${inheritedClasses}`,
      cardEyebrowClasses: `flex items-center text-gray-m7 uppercase text-lg font-bold leading-7 tracking-wider pb-3 max-sm:text-sm max-sm:leading-normal max-sm:tracking-normal gap-2`,
      cardEyebrowIconClasses: `fill-current`,
      cardHeaderClasses: `text-gray-m9 text-xl font-bold leading-[1.625rem] break-words m-0 !mb-[.67em]`,
      cardDescriptionClasses: `text-gray-m9 mb-8 text-base leading-relaxed font-normal [&_a]:underline`,
      cardContentClasses: `flex-grow pt-8 px-8 pb-0`,
      cardImageClasses: `w-full min-h-[200px] bg-cover bg-center text-transparent select-none rounded-t-[10px]`,
      cardLinkPositionClasses: `px-8 pb-8`,
      cardLinkClasses: `text-base font-semibold leading-relaxed tracking-normal no-underline text-blue-trmb gap-2 items-center`,
      boundCardImageSizes: boundCardImageSizes,
      unboundCardImageSizes: unboundCardImageSizes,
    },
    // Unbound Card Template
    unbound: {
      cardClasses: `t2-c w-full max-w-[300px] text-black t2-dark:text-white py-4 px-2 mb-5 flex flex-col ${inheritedClasses}`,
      cardEyebrowClasses: `flex items-center text-blue-trmb uppercase text-lg font-bold leading-7 tracking-wider pb-3 max-sm:text-sm max-sm:leading-normal max-sm:tracking-normal gap-2`,
      cardEyebrowIconClasses: `fill-blue-trmb`,
      cardHeaderClasses: `text-gray-m9 text-xl font-bold leading-[1.625rem] break-words m-0 !mb-[.67em]`,
      cardDescriptionClasses: `text-gray-m9 mb-8 text-base leading-relaxed font-normal [&_a]:underline`,
      cardContentClasses: `flex-grow pt-8 px-8 pb-0`,
      cardImageClasses: `w-full min-h-[200px] bg-cover bg-center text-transparent select-none rounded-t-[10px]`,
      cardLinkPositionClasses: `px-8 pb-8`,
      cardLinkClasses: `text-base font-semibold leading-relaxed tracking-normal no-underline text-blue-trmb gap-2 items-center`,
      boundCardImageSizes: boundCardImageSizes,
      unboundCardImageSizes: unboundCardImageSizes,
    },
    // Categorized Card Template
    categorized: {
      cardClasses: `t2-c w-full bg-gray-m1 shadow-gray-m1 max-w-[413px] t2-dark:text-black t2:bg-white t1:shadow-none mb-5 flex flex-col rounded-[10px] overflow-hidden ${useLightSetting} ${inheritedClasses}`,
      cardEyebrowClasses: `flex items-center text-blue-trmb uppercase text-lg font-bold leading-7 tracking-wider pb-3 max-sm:text-sm max-sm:leading-normal max-sm:tracking-normal gap-2`,
      cardEyebrowIconClasses: `fill-blue-trmb`,
      cardHeaderClasses: `text-gray-m9 text-xl font-bold leading-[1.625rem] break-words m-0 !mb-[.67em]`,
      cardDescriptionClasses: `text-gray-m9 mb-8 text-base leading-relaxed font-normal [&_a]:underline`,
      cardContentClasses: `flex-grow pt-8 px-8 pb-0`,
      cardImageClasses: `w-full min-h-[200px] bg-cover bg-center text-transparent select-none rounded-t-[10px]`,
      cardLinkPositionClasses: `px-8 pb-8`,
      cardLinkClasses: `text-base font-semibold leading-relaxed tracking-normal no-underline text-blue-trmb gap-2 items-center`,
      boundCardImageSizes: boundCardImageSizes,
      unboundCardImageSizes: unboundCardImageSizes,
    },
  }
  const focusParam = image?.focusArea ? formatFocusArea(image.focusArea) : null
  const { w, h } =
    cardMode === "unbound" ? CARD_SOURCES.unbound : CARD_SOURCES.bound
  const cardImageUrl = image?.url ? cardSrc(image.url, w, h, focusParam) : ""

  return (
    <div className={cardTemplate[cardMode].cardClasses}>
      {imageType !== "asImage" && image?.url && (
        <div
          className={cardTemplate[cardMode].cardImageClasses}
          style={{ backgroundImage: `url(${cardImageUrl || ""})` }}
        >
          {image?.alt}
        </div>
      )}
      {imageType === "asImage" &&
        renderFocusedOrRegularImage(
          image,
          cardMode == "bound" ? boundCardImageSizes : unboundCardImageSizes,
          `${cardTemplate[cardMode].cardImageClasses}`,
          90,
          componentImagePriority,
          isLCP,
        )}
      <div className={cardTemplate[cardMode].cardContentClasses}>
        {eyebrow && (
          <div className={cardTemplate[cardMode].cardEyebrowClasses}>
            {eyebrowIcon && (
              <Icon
                type={eyebrowIcon}
                size={20}
                className={cardTemplate[cardMode].cardEyebrowIconClasses}
              />
            )}
            {eyebrow}
          </div>
        )}
        {header && (
          <h3 className={cardTemplate[cardMode].cardHeaderClasses}>{header}</h3>
        )}
        {description && (
          <CommonDescription
            description={description}
            className={cardTemplate[cardMode].cardDescriptionClasses}
            truncate={truncate}
            truncateLength={truncateLength}
          />
        )}
      </div>
      <div className={cardTemplate[cardMode].cardLinkPositionClasses}>
        {button?.label && cardMode == "bound" && (
          <TerraLink
            {...button}
            linkType="text"
            isIcon={true}
            className={cardTemplate[cardMode].cardLinkClasses}
          />
        )}
        {button?.label && cardMode == "unbound" && (
          <TerraLink
            {...button}
            className={cardTemplate[cardMode].cardLinkClasses}
          />
        )}
        {button?.label && cardMode == "categorized" && (
          <SpecialButton
            {...button}
            className={cardTemplate[cardMode].cardLinkClasses}
          />
        )}
      </div>
    </div>
  )
}
